import Analytics from '@opencare/analytics';

import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import CONFIG from 'app.config';
import Logger from 'utils/Logger';

const analytics = new Analytics();

const createUrl = (url: string) => {
  try {
    return new URL(url);
  } catch {
    const path = url.startsWith('/') ? url : `/${url}`;
    return new URL(`${CONFIG.SEARCH_APP_V2_URL}${path}`);
  }
};

interface RedirectParams {
  url: string;
  reason: string;
  analyticsContent?: Record<string, string>;
}

type History = ReturnType<typeof useHistory>;

export class RedirectionService {
  #history: History;

  constructor({ history }: { history: History; }) {
    this.#history = history;
  }

  async redirect({ url, reason, analyticsContent = {} }: RedirectParams) {
    const finalUrl = createUrl(url);

    try {
      analytics.trackRedirect({
      // pageName is required, but we don't have a page name inside redirect
        pageName: '',
        pageType: analytics.pageTypes.MATCH_RESULTS,
        content: {
          ...analyticsContent,
          'redirect-reason': reason,
          'from-url': window.location.href,
          'to-url': finalUrl.toString(),
        },
      });

      const { host, pathname } = finalUrl;

      if (this.#history && host === CONFIG.SEARCH_APP_V2_URL) {
        this.#history.push(pathname);
        return;
      }

      await analytics.waitForQueue();

      window.location.assign(finalUrl.toString());
    } catch (error) {
      Logger.error('Error redirecting', error);
      window.location.assign(url);
    }
  }
}

export const useRedirectionService = () => {
  const history = useHistory();

  const createServiceParams = () => ({ history });
  const createService = () => new RedirectionService(createServiceParams());

  const [service, setService] = useState(createService());

  // Only update the service when the states change
  // This way we can use the entire service in useEffect watchers
  // To watch for any changes in the history state
  useEffect(
    () => setService(createService()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.values(createServiceParams()),
  );

  return service;
};
